import { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import {
  CheckCircle2,
  ChevronRight,
  Search,
  X,
  HelpCircle,
  History,
  Camera,
} from "lucide-react";
import { apiGet } from "../../shared/apiClient";
import type { Impianto, Chiamata } from "../../shared/types";
import { STATI_CHIAMATA, TIPI_IMPIANTO } from "../../shared/constants";
import { CollapsibleSection } from "../CollapsibleSection";
import { SecureImage } from "../SecureImage";
import { ReportInline } from "../ReportInline";

export function StoricoImpiantoSection() {
  const [searchStorico, setSearchStorico] = useState('');
  const [debouncedSearchStorico, setDebouncedSearchStorico] = useState('');
  const [selectedImpiantoStorico, setSelectedImpiantoStorico] = useState<Impianto | null>(null);
  const [previewStorico, setPreviewStorico] = useState<{ url: string; tipo: string; nome?: string } | null>(null);

  // Debounce ricerca impianti per storico report (300ms)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchStorico(searchStorico), 300);
    return () => clearTimeout(timer);
  }, [searchStorico]);

  // Query ricerca impianti per storico (tutti i tipi di impianto attivi)
  const impiantiStoricoQuery = useQuery({
    queryKey: ['impianti-storico-search', debouncedSearchStorico],
    enabled: !selectedImpiantoStorico && debouncedSearchStorico.trim().length >= 2,
    queryFn: async () =>
      (
        await apiGet<Impianto[]>('/impianti', {
          search: debouncedSearchStorico,
        })
      ).data,
  });

  const filteredStoricoImpianti = impiantiStoricoQuery.data || [];

  // Query ultimi 10 interventi con almeno 1 report squadra per l'impianto selezionato
  const storicoImpiantoQuery = useQuery({
    queryKey: ['impianto-storico-utilita', selectedImpiantoStorico?.codice],
    enabled: !!selectedImpiantoStorico?.codice,
    queryFn: async () => {
      const { data } = await apiGet<Chiamata[]>(`/impianti/${selectedImpiantoStorico!.codice}/storico`, {
        limit: 10,
        soloConReport: 'true',
      });
      return data;
    },
  });

  const storicoImpiantoItems = useMemo(() => {
    return (storicoImpiantoQuery.data || [])
      .filter((item) => (item.reports || []).some((r) => r.redattoDa !== 'operatore'))
      .slice(0, 10);
  }, [storicoImpiantoQuery.data]);

  return (
    <>
      <CollapsibleSection
        icon={History}
        iconClass="bg-amber-600 text-white"
        title="Storico Report per Impianto"
        subtitle="Cerca un impianto e consulta gli ultimi 10 report compilati dalle squadre"
        defaultOpen={false}
      >
        <div className="space-y-4 pt-1">
          {selectedImpiantoStorico ? (
            <div className="rounded-2xl border-2 border-amber-500 bg-linear-to-br from-amber-50/90 to-orange-50/50 p-4 shadow-2xs">
              <div className="flex items-start justify-between gap-3">
                <div className="space-y-1">
                  <div className="inline-flex items-center gap-1.5 text-xs font-extrabold text-amber-800 uppercase tracking-wider">
                    <CheckCircle2 className="w-4 h-4 text-amber-600" /> Impianto Selezionato
                  </div>
                  <div className="font-extrabold text-slate-900 text-base sm:text-lg">
                    {selectedImpiantoStorico.codice} · {selectedImpiantoStorico.nome}
                  </div>
                  {selectedImpiantoStorico.tipo && (
                    <div className="text-xs text-amber-900 font-medium">
                      Tipologia: {(TIPI_IMPIANTO as Record<string, string>)[selectedImpiantoStorico.tipo] || selectedImpiantoStorico.tipo} ({selectedImpiantoStorico.tipo})
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className="px-3 py-1.5 text-xs bg-white flex items-center gap-1 rounded-xl shrink-0 font-bold hover:bg-slate-50 border border-slate-300 text-slate-700 shadow-2xs transition"
                  onClick={() => {
                    setSelectedImpiantoStorico(null);
                    setSearchStorico('');
                  }}
                >
                  <X className="w-3.5 h-3.5" /> Cambia Impianto
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="relative">
                <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
                  <Search className="w-4 h-4" />
                </div>
                <input
                  className="w-full pl-11 pr-11 py-2.5 text-sm font-medium text-slate-900 bg-white border border-slate-300 rounded-2xl focus:ring-2 focus:ring-amber-500 focus:outline-hidden shadow-2xs transition"
                  value={searchStorico}
                  onChange={(e) => setSearchStorico(e.target.value)}
                  placeholder="Digita codice o nome impianto (es. 01001 o Via Roma)..."
                />
                {searchStorico && (
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1"
                    onClick={() => setSearchStorico('')}
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              {searchStorico.trim().length < 2 ? (
                <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-xs text-slate-500 flex items-center gap-2">
                  <HelpCircle className="w-4 h-4 text-slate-400 shrink-0" />
                  <span>Digita almeno 2 caratteri per cercare un impianto e visualizzarne i report.</span>
                </div>
              ) : impiantiStoricoQuery.isLoading ? (
                <div className="space-y-2">
                  <div className="h-14 bg-slate-100 animate-pulse rounded-2xl" />
                  <div className="h-14 bg-slate-100 animate-pulse rounded-2xl" />
                </div>
              ) : filteredStoricoImpianti.length === 0 ? (
                <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4 text-xs sm:text-sm text-slate-600 text-center font-medium">
                  Nessun impianto trovato per "{searchStorico}".
                </div>
              ) : (
                <div className="space-y-2 max-h-[280px] overflow-auto pr-1">
                  <div className="text-xs text-slate-500 font-semibold px-1">
                    Impianti trovati ({filteredStoricoImpianti.length}):
                  </div>
                  {filteredStoricoImpianti.map((imp) => (
                    <button
                      key={imp.id}
                      type="button"
                      className="w-full text-left rounded-2xl border border-slate-200 bg-white hover:border-amber-500 hover:bg-amber-50/40 active:bg-amber-100/60 p-3.5 transition flex items-center justify-between gap-3 shadow-2xs group"
                      onClick={() => {
                        setSelectedImpiantoStorico(imp);
                        setSearchStorico('');
                      }}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="font-bold text-sm text-slate-900 group-hover:text-amber-950">
                          {imp.codice} · {imp.nome}
                        </div>
                        {imp.tipo && (
                          <div className="text-xs text-slate-500 mt-0.5">
                            Tipologia: {(TIPI_IMPIANTO as Record<string, string>)[imp.tipo] || imp.tipo} ({imp.tipo})
                          </div>
                        )}
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-amber-600 shrink-0" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Lista ultimi 10 interventi con report dell'impianto selezionato */}
          {selectedImpiantoStorico && (
            <div className="space-y-3 pt-2 border-t border-slate-200">
              <div className="flex items-center justify-between px-1">
                <h4 className="text-xs sm:text-sm font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
                  <History className="w-4 h-4 text-amber-600" />
                  Ultimi 10 Report Squadra ({storicoImpiantoItems.length})
                </h4>
                <button
                  type="button"
                  className="text-xs text-amber-700 hover:text-amber-800 font-bold"
                  onClick={() => storicoImpiantoQuery.refetch()}
                >
                  Aggiorna
                </button>
              </div>

              {storicoImpiantoQuery.isLoading ? (
                <div className="space-y-2.5">
                  <div className="h-24 bg-slate-100 animate-pulse rounded-2xl" />
                  <div className="h-24 bg-slate-100 animate-pulse rounded-2xl" />
                </div>
              ) : storicoImpiantoItems.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/50 p-6 text-center space-y-1">
                  <p className="font-bold text-slate-700 text-sm">Nessun report trovato per questo impianto</p>
                  <p className="text-xs text-slate-500">Non risultano ancora report tecnici compilati dalle squadre su {selectedImpiantoStorico.codice}.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {storicoImpiantoItems.map((item) => {
                    let itemFotoDisservizio: string[] = [];
                    if (item.fotoDisservizio) {
                      try {
                        const parsed = typeof item.fotoDisservizio === 'string' ? JSON.parse(item.fotoDisservizio) : item.fotoDisservizio;
                        itemFotoDisservizio = Array.isArray(parsed) ? parsed : [];
                      } catch {
                        itemFotoDisservizio = [];
                      }
                    }

                    const itemReports = (item.reports || []).filter((r) => r.redattoDa !== 'operatore');

                    return (
                      <div key={item.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-2xs space-y-3">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="text-xs text-slate-400 font-semibold">
                              {new Date(item.dataOra).toLocaleString('it-IT')}
                            </div>
                            <div className="font-bold text-sm text-slate-900 mt-0.5">
                              {item.descrizione}
                            </div>
                            <div className="text-xs text-slate-500 mt-0.5">
                              Squadra: <span className="font-semibold text-slate-700">{item.squadra?.nome || itemReports[0]?.squadraNome || '—'}</span>
                            </div>
                          </div>
                          <span className="badge-grigio text-[10px] shrink-0">
                            {STATI_CHIAMATA[item.stato]?.label || item.stato}
                          </span>
                        </div>

                        {/* Foto stato disservizio all'arrivo */}
                        {itemFotoDisservizio.length > 0 && (
                          <div className="rounded-xl bg-amber-50/60 border border-amber-200/80 p-2.5 space-y-1.5">
                            <div className="flex items-center gap-1.5 text-[11px] font-bold text-amber-900 uppercase tracking-wider">
                              <Camera className="w-3.5 h-3.5 text-amber-600" />
                              <span>Foto Arrivo / Disservizio ({itemFotoDisservizio.length})</span>
                            </div>
                            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 pt-0.5">
                              {itemFotoDisservizio.map((fn, idx) => (
                                <div
                                  key={idx}
                                  className="relative group rounded-xl border border-amber-200 bg-white overflow-hidden aspect-square block shadow-2xs hover:shadow-md transition cursor-pointer"
                                  onClick={() =>
                                    setPreviewStorico({
                                      url: `/api/uploads/${fn}`,
                                      tipo: 'image/jpeg',
                                      nome: `Foto disservizio ${new Date(item.dataOra).toLocaleDateString('it-IT')} (${idx + 1}/${itemFotoDisservizio.length})`,
                                    })
                                  }
                                >
                                  <SecureImage
                                    src={`/api/uploads/${fn}`}
                                    alt={`Foto disservizio ${idx + 1}`}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                                    onImageClick={(blobUrl) => {
                                      setPreviewStorico({
                                        url: blobUrl,
                                        tipo: 'image/jpeg',
                                        nome: `Foto disservizio ${new Date(item.dataOra).toLocaleDateString('it-IT')} (${idx + 1}/${itemFotoDisservizio.length})`,
                                      });
                                    }}
                                  />
                                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white text-[10px] font-bold pointer-events-none">
                                    🔍 Zoom
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Report associati */}
                        {itemReports.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-slate-100 space-y-2">
                            <div className="text-xs font-bold text-slate-500">Report associati:</div>
                            {itemReports.map((rep) => (
                              <ReportInline
                                key={rep.id}
                                report={rep}
                                squadraNome={rep.squadraNome || item.squadra?.nome}
                                onPreview={(url, nome) => setPreviewStorico({ url, tipo: 'image/jpeg', nome })}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </CollapsibleSection>

      {/* ── MODALE DI ANTEPRIMA FOTO STORICO ── */}
      {previewStorico && typeof document !== 'undefined' && createPortal(
        <div className="fixed inset-0 z-[100] flex flex-col bg-slate-950/95 animate-fade-in">
          <div className="flex items-center justify-between p-3.5 bg-slate-900 border-b border-slate-800">
            <span className="text-white text-sm font-bold truncate pr-2">
              {previewStorico.nome || 'Anteprima allegato'}
            </span>
            <button
              type="button"
              className="btn-ghost px-3 py-1.5 text-white hover:bg-white/10 rounded-xl text-xs"
              onClick={() => {
                if (previewStorico?.url && previewStorico.url.startsWith('blob:')) URL.revokeObjectURL(previewStorico.url);
                setPreviewStorico(null);
              }}
            >
              <X className="w-4 h-4" /> Chiudi
            </button>
          </div>
          <div className="flex-1 overflow-auto p-2 flex items-center justify-center">
            <img src={previewStorico.url} alt="Allegato" className="max-w-full max-h-full object-contain rounded-lg" />
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
